import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';
import { 
  Search, Filter, TrendingUp, TrendingDown, DollarSign, Shield, 
  Percent, BarChart3, RefreshCw, ArrowLeft, Target, Zap,
  AlertTriangle, ChevronUp, ChevronDown, Award, LineChart,
  CandlestickChart, Maximize2
} from 'lucide-react';
import { toast } from 'sonner';
import TradingChartModal, { openChartInNewWindow } from './TradingChartModal';
import MarketTimer from './MarketTimer';
import DataSourcesPanel from './DataSourcesPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STRATEGY_OPTIONS = [
  { id: 'cash_secured_put', name: 'Cash-Secured Put', risk: 'Medium', type: 'credit' },
  { id: 'bull_put_spread', name: 'Bull Put Spread', risk: 'Low', type: 'credit' },
  { id: 'bear_call_spread', name: 'Bear Call Spread', risk: 'Low', type: 'credit' },
  { id: 'iron_condor', name: 'Iron Condor', risk: 'Low', type: 'credit' },
  { id: 'iron_butterfly', name: 'Iron Butterfly', risk: 'Medium', type: 'credit' },
  { id: 'short_straddle', name: 'Short Straddle', risk: 'High', type: 'credit' },
  { id: 'short_strangle', name: 'Short Strangle', risk: 'High', type: 'credit' },
  { id: 'long_straddle', name: 'Long Straddle', risk: 'Medium', type: 'debit' },
  { id: 'long_strangle', name: 'Long Strangle', risk: 'Medium', type: 'debit' },
  { id: 'long_call', name: 'Long Call', risk: 'Medium', type: 'debit' },
  { id: 'short_call', name: 'Short Call (Naked)', risk: 'High', type: 'credit' },
  { id: 'long_put', name: 'Long Put', risk: 'Medium', type: 'debit' },
  { id: 'short_put', name: 'Short Put (Naked)', risk: 'High', type: 'credit' },
];

const SORT_OPTIONS = [
  { id: 'composite_score', name: 'Best Overall', icon: Award },
  { id: 'credit', name: 'Highest Credit', icon: DollarSign },
  { id: 'probability_profit', name: 'Most Safe (Highest Probability)', icon: Shield },
  { id: 'roi_percent', name: 'Best ROI %', icon: Percent },
  { id: 'risk_reward', name: 'Best Risk/Reward', icon: Target },
  { id: 'collateral', name: 'Lowest Collateral', icon: TrendingDown },
];

const CreditScanner = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scannedStocks, setScannedStocks] = useState(0);
  const [cached, setCached] = useState(false);
  const [timestamp, setTimestamp] = useState('');
  const [chartSymbol, setChartSymbol] = useState('');
  const [isChartOpen, setIsChartOpen] = useState(false);
  const [quickChartSymbol, setQuickChartSymbol] = useState('');
  
  // Open chart modal
  const openChart = (symbol) => {
    if (symbol) {
      setChartSymbol(symbol.toUpperCase());
      setIsChartOpen(true);
    }
  };
  
  // Filters
  const [selectedStrategies, setSelectedStrategies] = useState(['all']);
  const [minProbability, setMinProbability] = useState(70);
  const [maxStocks, setMaxStocks] = useState(50);
  const [minDte, setMinDte] = useState(20);
  const [maxDte, setMaxDte] = useState(60);
  const [minCollateralFilter, setMinCollateralFilter] = useState(0);
  const [maxCollateralFilter, setMaxCollateralFilter] = useState(50000);
  const [includeDebit, setIncludeDebit] = useState(true);
  const [strategiesPerStock, setStrategiesPerStock] = useState(5); // Legacy: strategies per stock
  const [resultsPerStrategy, setResultsPerStrategy] = useState(5); // NEW: unique stocks per strategy type
  const [sortBy, setSortBy] = useState('composite_score');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Cost filter (NEW)
  const [minCost, setMinCost] = useState(0);
  const [maxCost, setMaxCost] = useState(0); // 0 = unlimited
  
  // Filter displayed results
  const [filterStrategy, setFilterStrategy] = useState('all');
  const [minCredit, setMinCredit] = useState(0);
  const [maxCredit, setMaxCredit] = useState(0); // 0 = unlimited
  const [maxCollateral, setMaxCollateral] = useState(100000);

  const runScan = async () => {
    // Validate at least one strategy is selected
    if (selectedStrategies.length === 0) {
      toast.error('Please select at least one strategy to scan');
      return;
    }
    
    setLoading(true);
    toast.info('Scanning market for opportunities... This may take 30-60 seconds.');
    
    try {
      // If no strategies selected but not 'all', use 'all' as default
      const strategiesToSend = selectedStrategies.length === 0 ? ['all'] : selectedStrategies;
      
      const response = await axios.post(`${API}/credit-scanner/scan`, {
        strategies: strategiesToSend,
        min_probability: minProbability,
        max_stocks: maxStocks,
        min_dte: minDte,
        max_dte: maxDte,
        min_collateral: minCollateralFilter,
        max_collateral: maxCollateralFilter,
        include_debit: includeDebit,
        strategies_per_stock: strategiesPerStock,
        min_cost: minCost,
        max_cost: maxCost,
        results_per_strategy: resultsPerStrategy // NEW: unique stocks per strategy
      }, { timeout: 120000 }); // 2 minute timeout
      
      // Distinguish an upstream market-data failure from a legitimate zero-result scan.
      if (response.data.status === 'error') {
        setResults([]);
        setScannedStocks(0);
        toast.error(response.data.message || 'Market data is temporarily unavailable. Please try again.');
        return;
      }

      setResults(response.data.results || []);
      setScannedStocks(response.data.stocks_scanned || 0);
      setCached(response.data.cached || false);
      setTimestamp(response.data.timestamp || '');
      
      if (response.data.cached) {
        toast.success(`Loaded ${response.data.results?.length || 0} cached opportunities`);
      } else {
        const uniqueStocks = new Set(response.data.results?.map(r => r.symbol)).size;
        const total = response.data.total_opportunities || 0;
        if (total === 0) {
          toast.info(`No qualifying strategies were found across ${response.data.stocks_scanned || 0} stocks scanned. Try loosening the filters.`);
        } else {
          toast.success(`Found ${total} top strategies across ${uniqueStocks} stocks!`);
        }
      }
    } catch (error) {
      console.error('Error scanning:', error);
      toast.error('Failed to scan market. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort results
  const filteredResults = results
    .filter(r => filterStrategy === 'all' || r.strategy_type === filterStrategy)
    .filter(r => {
      // For credit strategies, filter by credit amount range
      // For debit strategies, don't filter by credit
      if (r.position_type === 'debit') {
        return true; // Don't filter debit strategies by credit
      }
      // Check min credit
      if (r.credit < minCredit) return false;
      // Check max credit (0 = unlimited)
      if (maxCredit > 0 && r.credit > maxCredit) return false;
      return true;
    })
    .filter(r => typeof r.collateral === 'number' && r.collateral <= maxCollateral)
    .sort((a, b) => {
      let aVal = a[sortBy] || 0;
      let bVal = b[sortBy] || 0;
      
      // Handle "Unlimited" max_loss
      if (sortBy === 'collateral') {
        aVal = typeof a.collateral === 'number' ? a.collateral : 999999;
        bVal = typeof b.collateral === 'number' ? b.collateral : 999999;
      }
      
      if (sortDirection === 'desc') {
        return bVal - aVal;
      }
      return aVal - bVal;
    });

  const toggleStrategy = (strategyId) => {
    if (strategyId === 'all') {
      // If clicking "All", either select all or deselect all
      if (selectedStrategies.includes('all')) {
        // Uncheck all - set to empty (will show all unchecked)
        setSelectedStrategies([]);
      } else {
        // Check all
        setSelectedStrategies(['all']);
      }
    } else {
      setSelectedStrategies(prev => {
        // Remove 'all' if it was selected when clicking individual
        const filtered = prev.filter(s => s !== 'all');
        
        if (filtered.includes(strategyId)) {
          // Uncheck this strategy
          const newList = filtered.filter(s => s !== strategyId);
          return newList;
        } else {
          // Check this strategy
          const newList = [...filtered, strategyId];
          // If all individual strategies are selected, switch to 'all'
          if (newList.length === STRATEGY_OPTIONS.length) {
            return ['all'];
          }
          return newList;
        }
      });
    }
  };

  const getRiskBadge = (risk) => {
    switch (risk) {
      case 'Low':
        return <Badge className="bg-green-100 text-green-800">Low Risk</Badge>;
      case 'Medium':
        return <Badge className="bg-yellow-100 text-yellow-800">Medium Risk</Badge>;
      case 'High':
        return <Badge className="bg-red-100 text-red-800">High Risk</Badge>;
      default:
        return null;
    }
  };

  const getStrategyRisk = (strategyType) => {
    const strategy = STRATEGY_OPTIONS.find(s => s.id === strategyType);
    return strategy?.risk || 'Medium';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Market Timer */}
      <MarketTimer />

      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-amber-500/20 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-700/50">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="absolute inset-0 bg-amber-400 blur-lg opacity-30 rounded-xl"></div>
                  <div className="relative p-2.5 bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 rounded-xl shadow-lg border border-amber-300/30">
                    <Search className="h-7 w-7 text-slate-900" />
                  </div>
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                    <span className="bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 bg-clip-text text-transparent">
                      Strategy Scanner
                    </span>
                  </h1>
                  <p className="text-xs text-slate-400 tracking-wide uppercase" style={{ letterSpacing: '0.08em' }}>
                    Find Profitable Options Across Markets
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Quick Chart Input */}
              <div className="hidden md:flex items-center space-x-1 bg-slate-800/80 rounded-lg px-2 py-1 border border-slate-700">
                <Input
                  placeholder="Symbol"
                  value={quickChartSymbol}
                  onChange={(e) => setQuickChartSymbol(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && quickChartSymbol && openChart(quickChartSymbol)}
                  className="w-20 h-8 text-sm bg-transparent border-0 text-white placeholder:text-slate-500 focus-visible:ring-0"
                  data-testid="quick-chart-input"
                />
                <Button
                  onClick={() => quickChartSymbol && openChart(quickChartSymbol)}
                  size="sm"
                  className="h-8 px-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0"
                  title="Open chart in modal"
                >
                  <CandlestickChart className="h-4 w-4" />
                </Button>
                <Button
                  onClick={() => quickChartSymbol && openChartInNewWindow(quickChartSymbol)}
                  size="sm"
                  className="h-8 px-2 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white border-0"
                  title="Open chart in new window (side-by-side)"
                >
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </div>
              
              {timestamp && (
                <div className="text-right text-sm text-slate-400">
                  <p>Last scan: {new Date(timestamp).toLocaleString()}</p>
                  {cached && <Badge variant="outline" className="border-amber-500/50 text-amber-400">Cached</Badge>}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="glass-effect border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Filter className="h-5 w-5 mr-2 text-indigo-600" />
                  Scan Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Strategy Selection */}
                <div>
                  <Label className="text-sm font-semibold mb-3 block">Strategies to Scan</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 pb-2 border-b">
                      <Checkbox 
                        id="all-strategies"
                        checked={selectedStrategies.includes('all')} 
                        onCheckedChange={() => toggleStrategy('all')}
                      />
                      <label htmlFor="all-strategies" className="text-sm font-medium cursor-pointer">
                        All Strategies
                      </label>
                    </div>
                    {STRATEGY_OPTIONS.map(strategy => (
                      <div key={strategy.id} className="flex items-center justify-between py-1">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id={strategy.id}
                            checked={selectedStrategies.includes(strategy.id) || selectedStrategies.includes('all')} 
                            onCheckedChange={() => toggleStrategy(strategy.id)}
                          />
                          <label htmlFor={strategy.id} className="text-xs cursor-pointer">{strategy.name}</label>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Badge className={`text-[10px] px-1 py-0 ${
                            strategy.type === 'credit' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {strategy.type}
                          </Badge>
                          {getRiskBadge(strategy.risk)}
                        </div>
                      </div>
                    ))}
                  </div>
                  {selectedStrategies.length === 0 && (
                    <p className="text-xs text-amber-600 mt-2">⚠️ Please select at least one strategy</p>
                  )}
                </div>

                {/* Probability Filter */}
                <div>
                  <Label className="text-sm font-semibold mb-2 block">
                    Min Probability of Profit: {minProbability}%
                  </Label>
                  <Slider
                    value={[minProbability]}
                    onValueChange={(v) => setMinProbability(v[0])}
                    min={30}
                    max={95}
                    step={5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>30%</span>
                    <span>95%</span>
                  </div>
                </div>

                {/* Credit Received Range - MAIN FILTER */}
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <Label className="text-sm font-semibold mb-2 block text-green-700 flex items-center">
                    <DollarSign className="h-4 w-4 mr-1" />
                    Credit Received Range
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-green-600">Min Credit ($)</Label>
                      <Input 
                        type="number" 
                        value={minCredit} 
                        onChange={(e) => setMinCredit(parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        min={0}
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-green-600">Max Credit ($)</Label>
                      <Input 
                        type="number" 
                        value={maxCredit || ''} 
                        onChange={(e) => setMaxCredit(parseFloat(e.target.value) || 0)}
                        placeholder="No limit"
                        min={0}
                        className="bg-white"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    Filter by premium received (0 max = unlimited)
                  </p>
                </div>

                {/* Collateral Range */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Min Collateral ($)</Label>
                    <Input 
                      type="number" 
                      value={minCollateralFilter} 
                      onChange={(e) => setMinCollateralFilter(parseInt(e.target.value) || 0)}
                      min={0}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Max Collateral ($)</Label>
                    <Input 
                      type="number" 
                      value={maxCollateralFilter} 
                      onChange={(e) => setMaxCollateralFilter(parseInt(e.target.value) || 50000)}
                      min={100}
                      placeholder="50000"
                    />
                  </div>
                </div>

                {/* Include Debit Strategies */}
                <div className="flex items-center space-x-2 bg-blue-50 p-3 rounded-lg">
                  <Checkbox 
                    checked={includeDebit} 
                    onCheckedChange={setIncludeDebit}
                  />
                  <div>
                    <span className="text-sm font-medium">Include Debit Strategies</span>
                    <p className="text-xs text-gray-500">Long Call, Long Put</p>
                  </div>
                </div>

                {/* NEW: Cost of Strategy Filter */}
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <Label className="text-sm font-semibold mb-2 block text-purple-700 flex items-center">
                    <DollarSign className="h-4 w-4 mr-1" />
                    Cost of Strategy (Collateral)
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-purple-600">Min Cost ($)</Label>
                      <Input 
                        type="number" 
                        value={minCost || ''} 
                        onChange={(e) => setMinCost(parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        min={0}
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-purple-600">Max Cost ($)</Label>
                      <Input 
                        type="number" 
                        value={maxCost || ''} 
                        onChange={(e) => setMaxCost(parseFloat(e.target.value) || 0)}
                        placeholder="No limit"
                        min={0}
                        className="bg-white"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-purple-600 mt-1">
                    Filter by required capital/collateral (0 = no limit)
                  </p>
                </div>

                {/* Stocks to Scan */}
                <div>
                  <Label className="text-sm font-semibold mb-2 block">
                    Stocks to Scan: {maxStocks}
                  </Label>
                  <Slider
                    value={[maxStocks]}
                    onValueChange={(v) => setMaxStocks(v[0])}
                    min={10}
                    max={100}
                    step={10}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>10</span>
                    <span>100</span>
                  </div>
                </div>

                {/* Unique Stocks per Strategy - Primary diversity control */}
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-300">
                  <Label className="text-sm font-semibold mb-2 block text-amber-700">
                    🎯 Unique Stocks per Strategy: {resultsPerStrategy === 0 ? 'Unlimited' : resultsPerStrategy}
                  </Label>
                  <Select 
                    value={resultsPerStrategy.toString()} 
                    onValueChange={(v) => setResultsPerStrategy(parseInt(v))}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 stock per strategy (Most diverse)</SelectItem>
                      <SelectItem value="3">3 stocks per strategy</SelectItem>
                      <SelectItem value="5">5 stocks per strategy (Default)</SelectItem>
                      <SelectItem value="10">10 stocks per strategy</SelectItem>
                      <SelectItem value="0">Unlimited</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-amber-600 mt-1">
                    <strong>AI picks the BEST opportunity</strong> for each stock per strategy type. Ensures variety!
                  </p>
                </div>

                {/* DTE Range */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Min DTE</Label>
                    <Input 
                      type="number" 
                      value={minDte} 
                      onChange={(e) => setMinDte(parseInt(e.target.value) || 0)}
                      min={1}
                      max={90}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Max DTE</Label>
                    <Input 
                      type="number" 
                      value={maxDte} 
                      onChange={(e) => setMaxDte(parseInt(e.target.value) || 60)}
                      min={1}
                      max={180}
                    />
                  </div>
                </div>

                {/* Scan Button */}
                <Button 
                  onClick={runScan} 
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Scanning {scannedStocks > 0 ? `(${scannedStocks} stocks)` : '...'}
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      Scan Market
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Results Filter */}
            {results.length > 0 && (
              <Card className="glass-effect border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Filter className="h-4 w-4 mr-2" />
                    Filter Results
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-xs">Strategy Type</Label>
                    <Select value={filterStrategy} onValueChange={setFilterStrategy}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Strategies</SelectItem>
                        {STRATEGY_OPTIONS.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Credit Received Range */}
                  <div className="bg-green-50 p-3 rounded-lg space-y-3">
                    <Label className="text-sm font-semibold flex items-center text-green-700">
                      <DollarSign className="h-4 w-4 mr-1" />
                      Credit Received Filter
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-green-600">Min Credit ($)</Label>
                        <Input 
                          type="number" 
                          value={minCredit} 
                          onChange={(e) => setMinCredit(parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          min={0}
                          className="bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-green-600">Max Credit ($)</Label>
                        <Input 
                          type="number" 
                          value={maxCredit || ''} 
                          onChange={(e) => setMaxCredit(parseFloat(e.target.value) || 0)}
                          placeholder="Unlimited"
                          min={0}
                          className="bg-white"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-green-600">
                      {maxCredit > 0 
                        ? `Showing credits between $${minCredit} - $${maxCredit}` 
                        : `Showing credits ≥ $${minCredit} (no max limit)`}
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Max Collateral/Cost ($)</Label>
                    <Input 
                      type="number" 
                      value={maxCollateral} 
                      onChange={(e) => setMaxCollateral(parseFloat(e.target.value) || 100000)}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Multi-Source Data Panel */}
            <DataSourcesPanel />
          </div>

          {/* Results */}
          <div className="lg:col-span-3">
            {/* Sort Controls */}
            {results.length > 0 && (
              <Card className="glass-effect border-0 shadow-lg mb-6">
                <CardContent className="py-4">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center space-x-3">
                      <Label className="text-sm font-semibold">Sort by:</Label>
                      <div className="flex flex-wrap gap-2">
                        {SORT_OPTIONS.map(option => (
                          <Button
                            key={option.id}
                            variant={sortBy === option.id ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              if (sortBy === option.id) {
                                setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
                              } else {
                                setSortBy(option.id);
                                setSortDirection(option.id === 'collateral' ? 'asc' : 'desc');
                              }
                            }}
                            className="text-xs"
                          >
                            <option.icon className="h-3 w-3 mr-1" />
                            {option.name}
                            {sortBy === option.id && (
                              sortDirection === 'desc' ? 
                                <ChevronDown className="h-3 w-3 ml-1" /> : 
                                <ChevronUp className="h-3 w-3 ml-1" />
                            )}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      Showing {filteredResults.length} of {results.length} opportunities
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Results List */}
            {loading ? (
              <Card className="glass-effect border-0 shadow-lg">
                <CardContent className="flex flex-col items-center justify-center py-20">
                  <RefreshCw className="h-16 w-16 text-indigo-400 animate-spin mb-4" />
                  <p className="text-lg font-semibold text-gray-700">Scanning {maxStocks} stocks...</p>
                  <p className="text-sm text-gray-500 mt-2">Analyzing all credit strategies with ≥{minProbability}% probability</p>
                  <p className="text-xs text-gray-400 mt-4">This may take 30-60 seconds</p>
                </CardContent>
              </Card>
            ) : results.length === 0 ? (
              <Card className="glass-effect border-0 shadow-lg">
                <CardContent className="flex flex-col items-center justify-center py-20">
                  <Search className="h-16 w-16 text-gray-400 mb-4" />
                  <p className="text-lg font-semibold text-gray-700">No Results Yet</p>
                  <p className="text-sm text-gray-500 mt-2">Configure your scan settings and click &quot;Scan Market&quot;</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4 pb-8">
                {filteredResults.map((result, index) => (
                  <Card 
                    key={`${result.symbol}-${result.strategy_type}-${index}`}
                    className={`glass-effect border-0 shadow-lg hover:shadow-xl transition-shadow ${
                      index < 3 ? 'ring-2 ring-yellow-400' : ''
                    }`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          {index < 3 && (
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                              index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-amber-600'
                            }`}>
                              {index + 1}
                            </div>
                          )}
                          <div>
                            <div className="flex items-center space-x-2">
                              <h3 className="text-xl font-bold text-gray-900">{result.symbol}</h3>
                              <Badge variant="outline">{result.strategy}</Badge>
                              <Badge className={`text-[10px] ${
                                result.position_type === 'debit' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {result.position_type === 'debit' ? 'DEBIT' : 'CREDIT'}
                              </Badge>
                              {getRiskBadge(getStrategyRisk(result.strategy_type))}
                              {/* Chart buttons for this stock */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openChart(result.symbol)}
                                className="ml-2 h-6 px-2 text-xs bg-blue-50 hover:bg-blue-100 border-blue-200"
                                title="View chart in modal"
                              >
                                <CandlestickChart className="h-3 w-3 mr-1" />
                                Chart
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openChartInNewWindow(result.symbol)}
                                className="h-6 px-2 text-xs bg-purple-50 hover:bg-purple-100 border-purple-200"
                                title="Open chart in new window"
                              >
                                <Maximize2 className="h-3 w-3" />
                              </Button>
                            </div>
                              <p className="text-sm text-gray-500">{result.name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            {result.position_type === 'debit' ? (
                              <>
                                <div className="text-2xl font-bold text-blue-600">${result.debit || result.collateral}</div>
                                <div className="text-xs text-gray-500">Cost (Debit)</div>
                              </>
                            ) : (
                              <>
                                <div className="text-2xl font-bold text-green-600">${result.credit}</div>
                                <div className="text-xs text-gray-500">Credit Received</div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="bg-gradient-to-r from-slate-50 to-indigo-50 rounded-lg p-4 mb-4">
                          <p className="text-sm font-mono text-gray-700">{result.legs}</p>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                            <span>Stock: ${result.current_price}</span>
                            <span>Exp: {result.expiration}</span>
                            <span>{result.dte} DTE</span>
                            <span>IV: {result.iv}%</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                          <div className="bg-green-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-gray-600">Max Profit</p>
                            <p className="text-lg font-bold text-green-600">
                              {result.max_profit === 'Unlimited' ? '∞' : `$${result.max_profit}`}
                            </p>
                          </div>
                          <div className="bg-red-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-gray-600">Max Loss</p>
                            <p className="text-lg font-bold text-red-600">
                              {result.max_loss === 'Unlimited' ? '∞' : `$${result.max_loss}`}
                            </p>
                          </div>
                          <div className="bg-blue-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-gray-600">Probability</p>
                            <p className="text-lg font-bold text-blue-600">{result.probability_profit}%</p>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-gray-600">ROI</p>
                            <p className="text-lg font-bold text-purple-600">{result.roi_percent}%</p>
                          </div>
                          <div className="bg-orange-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-gray-600">Collateral</p>
                            <p className="text-lg font-bold text-orange-600">${result.collateral}</p>
                          </div>
                        </div>

                        {result.breakeven && (
                          <div className="mt-3 text-sm text-gray-600">
                            <span className="font-medium">Breakeven:</span> {result.breakeven}
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-4 pt-4 border-t">
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>Credit Rank: #{result.rank_credit || '-'}</span>
                            <span>Safety Rank: #{result.rank_safety || '-'}</span>
                            <span>ROI Rank: #{result.rank_roi || '-'}</span>
                          </div>
                          <div className="flex items-center space-x-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setChartSymbol(result.symbol);
                                setIsChartOpen(true);
                              }}
                              className="text-xs bg-blue-50 border-blue-200 hover:bg-blue-100"
                            >
                              <LineChart className="h-3 w-3 mr-1" />
                              View Chart
                            </Button>
                            <div className="flex items-center space-x-2">
                              <Award className="h-4 w-4 text-yellow-500" />
                              <span className="text-sm font-semibold">Score: {result.composite_score}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Trading Chart Modal */}
      <TradingChartModal
        isOpen={isChartOpen}
        onClose={() => setIsChartOpen(false)}
        symbol={chartSymbol}
      />
    </div>
  );
};

export default CreditScanner;
